import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box, Typography, Container, Alert,
  Radio, RadioGroup, FormControlLabel, FormLabel,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const SalesPartnerOnboarding = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();

  const [type, setType] = useState('individual');
  const [formData, setFormData] = useState({});
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  /** 🔹 Contact dialog states */
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contact, setContact] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState(false);

  /** 🔹 Fetch existing status based on type */
  const fetchStatusForType = async (selectedType) => {
    try {
      const res = await axios.post(`${API_URL}/api/sales-partner/status/`, { user_id: userId });
      const isCompany = !!res.data.gst_no;

      if ((selectedType === 'company' && isCompany) || (selectedType === 'individual' && !isCompany)) {
        setFormData(res.data);
        setStatus(res.data.status || '');
        setAgreementChecked(true);
      } else {
        setFormData({});
        setStatus('');
        setAgreementChecked(false);
      }
    } catch {
      setFormData({});
      setStatus('');
      setAgreementChecked(false);
    }
  };

  useEffect(() => {
    fetchStatusForType(type);
  }, []);

  const handleTypeChange = async (e) => {
    const newType = e.target.value;
    setType(newType);
    await fetchStatusForType(newType);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckboxChange = (e) => {
    setAgreementChecked(e.target.checked);
  };

  const handleAgreementClick = (e) => {
    e.preventDefault();
    navigate(type === 'individual' ? '/sales-partner-agreement' : '/sales-partner-agreement-company');
  };

  const handleDialogClose = () => setDialogOpen(false);

  /** 🔹 Step 1: Before submit, check contact */
  const handleOpenDialog = async (e) => {
    e.preventDefault();

    try {
      const res = await axios.post(`${API_URL}/api/user/check-contact/`, { user_id: userId });

      if (!res.data.has_contact) {
        setPendingSubmit(true);
        setContactDialogOpen(true);
        return;
      }

      setDialogOpen(true);
    } catch {
      setMessage('Unable to verify contact information.');
    }
  };

  /** 🔹 Save contact if missing */
  const handleSaveContact = async () => {
    if (!contact.trim()) return;

    try {
      await axios.post(`${API_URL}/api/user/update-contact/`, {
        user_id: userId,
        contact
      });

      setContactDialogOpen(false);

      if (pendingSubmit) {
        setPendingSubmit(false);
        setDialogOpen(true);
      }
    } catch {
      setMessage('Failed to save contact.');
    }
  };

  /** 🔹 Step 2: Final submit/update */
  const handleSubmitConfirmed = async () => {
    const payload = { ...formData, user_id: userId };
    const url = type === 'individual'
      ? `${API_URL}/api/sales-partner/individual/`
      : `${API_URL}/api/sales-partner/company/`;

    try {
      await axios.post(url, payload);
      setMessage('Submitted successfully');
      setDialogOpen(false);
      fetchStatusForType(type);
    } catch {
      setMessage('Submission failed');
      setDialogOpen(false);
    }
  };

  return (
    <div>
      <Header1 />
      <Container maxWidth="sm" sx={{ mt: 5, minHeight: '100vh', background: 'radial-gradient(circle at top, #0f0f16 0%, #000000 100%)', }}>

        <Typography
          variant="h3"
          component="h1"
          align="center"
          fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            mt: 10,
            letterSpacing: 7,
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
          gutterBottom
        >
          Sales Partner Onboarding
        </Typography>

        {status && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Current Status: {status}
          </Alert>
        )}
        {message && (
          <Alert sx={{ my: 2 }} severity={message.includes('success') ? "success" : "error"}>
            {message}
          </Alert>
        )}

        <FormLabel component="legend" sx={{ mt: 2 }}>Type</FormLabel>
        <RadioGroup row value={type} onChange={handleTypeChange}>
          <FormControlLabel value="individual" control={<Radio />} label="Individual" />
          <FormControlLabel value="company" control={<Radio />} label="Company" />
        </RadioGroup>

        <Box component="form" onSubmit={handleOpenDialog} sx={{ mt: 2 }}>
          {type === 'individual' ? (
            <TextField
              name="govt_id"
              label="Government ID"
              fullWidth
              required
              value={formData.govt_id || ''}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          ) : (
            <>
              <TextField
                name="gst_no"
                label="GST Number"
                fullWidth
                required
                value={formData.gst_no || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                name="company_name"
                label="Company Name"
                fullWidth
                required
                value={formData.company_name || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                name="company_address"
                label="Company Address"
                fullWidth
                required
                multiline
                rows={3}
                value={formData.company_address || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={agreementChecked}
                onChange={handleCheckboxChange}
                name="agreement"
                color="primary"
              />
            }
            label={
              <span>
                I agree to the{' '}
                <a
                  href={type === 'individual' ? "/sales-partner-agreement" : "/sales-partner-agreement-company"}
                  onClick={handleAgreementClick}
                  style={{ color: '#1976d2', opacity: 0.8, textDecoration: 'underline' }}
                >
                  Sales Partner Agreement
                </a>
              </span>
            }
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={!agreementChecked}
          >
            {status ? 'Update' : 'Submit'}
          </Button>
        </Box>
      </Container>

      {/* 🔹 Contact Dialog */}
      <Dialog open={contactDialogOpen}>
        <DialogTitle>Contact Required</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide your contact number to continue.
          </DialogContentText>
          <TextField
            fullWidth
            label="Contact Number"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveContact} variant="contained">
            Save & Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔹 Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {status ? 'update' : 'submit'} your Sales Partner application as a{' '}
            <strong>{type}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmitConfirmed} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Footer2 />
    </div>
  );
};

export default SalesPartnerOnboarding;
